/**
 * Entity colour — the one place a stored `color` becomes something CSS can use.
 *
 * See docs/data-schemas.md, "Entity colour — the EntityColor contract".
 *
 * An entity's stored colour is a TOKEN KEY ('categorical-1'..'categorical-10'), or a
 * legacy hex from before the palette existed, or null. It is never a `var()` string:
 * storing one breaks any consumer that treats the value as a hex, which is a real bug
 * this codebase already had (`b.color + '20'` in BoardView produced `var(--x)20`, an
 * invalid value the browser drops, so those tints silently rendered as nothing).
 *
 * Keys resolve to `var(--categorical-N)`, which carries a light and a dark value, so an
 * entity's colour finally follows the theme. Hexes pass through untouched — a colour a
 * user hand-picked is their data, not drift.
 */

/** The palette, in picker order. Order is a contract: append only, never insert. */
export const CATEGORICAL_KEYS = [
    'categorical-1',
    'categorical-2',
    'categorical-3',
    'categorical-4',
    'categorical-5',
    'categorical-6',
    'categorical-7',
    'categorical-8',
    'categorical-9',
    'categorical-10',
];

/** Human labels, for pickers and screen readers. */
export const CATEGORICAL_LABELS = {
    'categorical-1': 'Green',
    'categorical-2': 'Violet',
    'categorical-3': 'Amber',
    'categorical-4': 'Red',
    'categorical-5': 'Pink',
    'categorical-6': 'Blue',
    'categorical-7': 'Indigo',
    'categorical-8': 'Orange',
    'categorical-9': 'Cyan',
    'categorical-10': 'Neutral',
};

/** Default for a new entity. Violet = the brand hue, matching the previous #7C3AED. */
export const DEFAULT_ENTITY_COLOR = 'categorical-2';

/**
 * Legacy hex -> key, for the v5 migration and for rendering old values consistently.
 *
 * These are the hexes the audit actually found in the codebase, mapped to the palette
 * entry that replaced them. Deliberately NOT a nearest-colour algorithm: only exact,
 * known values migrate. Anything else is a colour someone chose on purpose and is left
 * alone. Keys are compared upper-case.
 */
export const LEGACY_HEX_TO_KEY = {
    '#10B981': 'categorical-1',   // emerald, the most-used entity hex (x42)
    '#22C55E': 'categorical-1',   // green-500 variant
    '#84CC16': 'categorical-1',   // lime, nearest green
    '#8B5CF6': 'categorical-2',   // violet — is brand-500
    '#7C3AED': 'categorical-2',   // brand-600, the old project default
    '#A855F7': 'categorical-2',   // brand-light
    '#F59E0B': 'categorical-3',   // amber
    '#EAB308': 'categorical-3',   // yellow, nearest amber
    '#EF4444': 'categorical-4',   // red
    '#EC4899': 'categorical-5',   // pink
    '#2383E2': 'categorical-6',   // the pre-rebrand Notion blue
    '#3B82F6': 'categorical-6',   // blue-500
    '#0EA5E9': 'categorical-6',   // sky, nearest blue
    '#337EA9': 'categorical-6',   // Notion blue dark variant
    '#6366F1': 'categorical-7',   // indigo
    '#F97316': 'categorical-8',   // orange
    '#06B6D4': 'categorical-9',   // cyan
    '#14B8A6': 'categorical-9',   // teal — no teal ramp exists, cyan is nearest
    '#6B7280': 'categorical-10',  // gray
    '#9CA3AF': 'categorical-10',  // gray-400
    '#64748B': 'categorical-10',  // slate
};

const KEY_SET = new Set(CATEGORICAL_KEYS);

/** True if `value` is a categorical token key this build knows about. */
export function isCategoricalKey(value) {
    return typeof value === 'string' && KEY_SET.has(value);
}

/**
 * Turn a stored EntityColor into something you can put in a CSS value.
 *
 * @param {string|null|undefined} stored  the entity's `color` field
 * @param {string|null} fallback          used when stored is empty. Pass a token
 *                                        (`'var(--text-muted)'`), not a hex.
 * @returns {string|null}
 */
export function resolveEntityColor(stored, fallback = null) {
    if (isCategoricalKey(stored)) return `var(--${stored})`;

    if (typeof stored === 'string' && stored.trim()) {
        const t = stored.trim();
        // Already a CSS value (a var(), a hex, rgb(), a named colour). Pass it through.
        // Old data legitimately holds hexes and must keep rendering.
        return t;
    }

    return fallback;
}

/**
 * A translucent wash of an entity's colour — a card tint, a chip background.
 *
 * Use this instead of concatenating an alpha suffix onto the value. `color + '20'` only
 * works if the value is a 6-digit hex; it produces invalid CSS for a `var()`, an rgb(),
 * or a 3-digit hex, and an invalid value means the whole declaration is dropped rather
 * than degraded. color-mix() is well-defined for every shape the contract allows.
 *
 * @param {string|null|undefined} stored
 * @param {number} percent   0-100, how much of the entity colour to keep
 * @param {string} over      what to mix into — 'transparent' or a surface token
 */
export function entityColorWash(stored, percent = 12, over = 'transparent') {
    const c = resolveEntityColor(stored);
    if (!c) return over === 'transparent' ? 'transparent' : over;
    return `color-mix(in srgb, ${c} ${percent}%, ${over})`;
}

/**
 * Normalise a stored value to a key where it is safe to do so. Used by the v5 migration.
 * Returns the input unchanged when it is not a recognised legacy hex — an unrecognised
 * hex is a deliberate user choice, and snapping it to the nearest palette hue would
 * destroy data to satisfy a tidiness goal.
 */
export function normaliseEntityColor(stored) {
    if (isCategoricalKey(stored)) return stored;
    if (typeof stored !== 'string') return stored;
    const hit = LEGACY_HEX_TO_KEY[stored.trim().toUpperCase()];
    return hit || stored;
}
